/* eslint-disable @typescript-eslint/no-explicit-any */
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import React from 'react';
import { WizardView } from './internal/WizardView';
/**
 * A component that renders the main content of a multi-step wizard inside a dialog.
 * Manages internal wizard state, handles navigation between steps, and submits aggregated data.
 *
 * @template RootConfig - Configuration type that extends WidgetConfigBase, defaults to WidgetFormConfig.
 *
 * @param {DialogWizardContentProps} root0 - The props for the DialogWizardContent component.
 * @param {string} root0.title - The title displayed at the top of the wizard.
 * @param {WizardStep<RootConfig>[]} root0.steps - Array of steps that define the wizard flow.
 * @param {string} [root0.submitButtonLabel] - Optional custom label for the submit button.
 * @param {string} [root0.cancelButtonLabel] - Optional custom label for the cancel button.
 * @param {any} [root0.data] - Optional initial data to populate the wizard forms.
 * @param {() => void} [root0.onCancel] - Callback executed when the wizard is canceled.
 * @param {(changed: boolean) => void} [root0.onChanged] - Callback triggered when step data is changed.
 * @param {(data: any) => Promise<any>} root0.onSubmit - Callback executed when wizard is submitted.
 * @param {(data: any, response: any) => void} [root0.onSubmitSuccess] - Callback triggered after successful submission.
 * @param {(data: any) => void} [root0.onDataChange] - Callback triggered when aggregated wizard data changes.
 *
 * @returns {React.ReactElement | null} The rendered wizard view or null if no steps are provided.
 */
export const DialogWizardContent = ({ title, steps, submitButtonLabel, cancelButtonLabel, data: initialData, onCancel, onChanged, onSubmit, onSubmitSuccess, onDataChange, }) => {
    const stepComponentRef = React.useRef(null);
    const [state, setState] = React.useState({ step: 0, data: initialData });
    const [submitting, setSubmitting] = React.useState(false);
    const handleBack = React.useCallback(() => {
        setState((prevState) => {
            var _a;
            if (prevState.step > 0) {
                return {
                    step: prevState.step - 1,
                    data: Object.assign(Object.assign({}, prevState.data), (_a = stepComponentRef.current) === null || _a === void 0 ? void 0 : _a.getData()),
                };
            }
            else {
                return prevState;
            }
        });
    }, []);
    const handleNext = React.useCallback(() => {
        var _a;
        (_a = stepComponentRef.current) === null || _a === void 0 ? void 0 : _a.submit().catch(handleErrorDialog);
    }, []);
    const handleChanged = React.useCallback(() => {
        var _a;
        if (onChanged) {
            onChanged(true);
        }
        if (onDataChange) {
            onDataChange((_a = stepComponentRef.current) === null || _a === void 0 ? void 0 : _a.getData());
        }
    }, [onChanged, onDataChange]);
    const handleSubmit = React.useCallback((stepData) => {
        return Promise.resolve(stepData);
    }, []);
    const handleSubmitSuccess = React.useCallback((stepData) => {
        setState((prevState) => {
            if (prevState.step < steps.length - 1) {
                return {
                    step: prevState.step + 1,
                    data: Object.assign(Object.assign({}, prevState.data), stepData),
                };
            }
            else {
                return Object.assign(Object.assign({}, prevState), { result: Object.assign(Object.assign({}, prevState.data), stepData) });
            }
        });
        if (onChanged) {
            onChanged(true);
        }
    }, [steps.length, onChanged]);
    React.useEffect(() => {
        if (state.result) {
            setSubmitting(true);
            onSubmit(state.result)
                .then((response) => {
                setSubmitting(false);
                if (onSubmitSuccess) {
                    onSubmitSuccess(state.result, response);
                }
                return response;
            })
                .catch((error) => {
                setSubmitting(false);
                handleErrorDialog(error);
            });
        }
    }, [state.result, onSubmit, onSubmitSuccess]);
    if (steps.length > 0) {
        const step = steps[state.step];
        return (React.createElement(WizardView, { title: title, steps: steps, step: step, stepComponentRef: stepComponentRef, state: state, submitting: submitting, onBack: handleBack, onNext: handleNext, onCancel: onCancel, onChanged: handleChanged, onSubmit: handleSubmit, onSubmitSuccess: handleSubmitSuccess, cancelButtonLabel: cancelButtonLabel, submitButtonLabel: submitButtonLabel }));
    }
    else {
        return null;
    }
};
